import styled from '@emotion/styled';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import {
  IconArrowRight,
  IconLogInCircle,
} from '../../common/design-tokens/Icons';
import { t } from '../../localization/LocaleContext';
import { useResetPassword } from '../../remoteData/unauthenticated';
import PasswordField from './PasswordField';
import {
  Container,
  ErrorBlock,
  InputContainer,
  Inputs,
  LoginLink,
  SubmitButton,
  Text,
  Title,
} from './styledComponents';

const MIN_PASSWORD_LENGTH = 6;

const SubmitIcon = styled(IconArrowRight)`
  svg {
    path {
      fill: white;
    }
  }
`;

function passwordsMatch({
  password: p1,
  passwordConfirmation: p2,
}: {
  password: string;
  passwordConfirmation: string;
}) {
  return !!p1 && !!p2 && p1 === p2;
}

function passwordLengthOk(password: string) {
  return password.length > MIN_PASSWORD_LENGTH;
}

export default function PasswordReset() {
  useAnonymousAnalyticsTrackPage({
    name: 'Reset Password',
    category: 'Public',
  });

  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('t') ?? undefined;

  const [passwords, setPasswords] = useState({
    password: '',
    passwordConfirmation: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);

  const [passwordErrors, setPasswordErrors] = useState<{
    [k in keyof typeof passwords]?: string;
  }>({});

  const intl = useIntl();

  const {
    mutate: resetPassword,
    error: resetPasswordError,
    isSuccess: resetPasswordSuccess,
  } = useResetPassword();

  function validatePasswordsMatch() {
    if (passwordsMatch(passwords)) {
      setPasswordErrors({
        password: undefined,
        passwordConfirmation: undefined,
      });
    } else {
      setPasswordErrors({ passwordConfirmation: 'Passwords do not match' });
    }
  }

  function validatePasswordLength(
    password: 'password' | 'passwordConfirmation',
  ) {
    if (!passwordLengthOk(passwords[password])) {
      setPasswordErrors({ ...passwordErrors, [password]: 'Too short' });
    }
  }

  const errorsInForm =
    !passwordLengthOk(passwords.password) || !passwordsMatch(passwords);

  if (resetPasswordSuccess) {
    return (
      <Container narrow>
        <Title>{t('reset_password_success_title')}</Title>
        <Text>{t('reset_password_success_text')}</Text>
        <LoginLink to="/dashboard">
          <IconLogInCircle /> {t('reset_password_success_login_link')}
        </LoginLink>
      </Container>
    );
  }
  if (!urlToken) {
    return (
      <Container narrow>
        <Title>{t('general_invalid_link_title')}</Title>
        <Text>{t('general_invalid_link_text')}</Text>
      </Container>
    );
  }

  return (
    <Container narrow>
      <Title>{t('reset_password_title')}</Title>

      <form
        method="post"
        action="/api/account/reset-password"
        onSubmit={(event) => {
          event.preventDefault();
          resetPassword({
            token: urlToken,
            password: passwords.password,
            passwordConfirmation: passwords.passwordConfirmation,
          });
        }}
      >
        <Inputs>
          {(['password', 'passwordConfirmation'] as const).map((field) => (
            <InputContainer key={field}>
              <PasswordField
                key={field}
                name={field}
                value={passwords[field]}
                error={passwordErrors[field]}
                inputProps={{
                  minLength: MIN_PASSWORD_LENGTH,
                  style: { paddingRight: 32 },
                  autoComplete: 'new-password',
                }}
                label={intl.formatMessage({
                  id:
                    field === 'password'
                      ? 'general_password_fieldtext'
                      : 'general_confirm_password_fieldtext',
                })}
                onChange={(event) => {
                  const updatedPasswords = {
                    ...passwords,
                    [field]: event.target.value,
                  };
                  setPasswords(updatedPasswords);
                }}
                onBlur={() => {
                  validatePasswordsMatch();
                  validatePasswordLength(field);
                }}
                showPassword={showPasswords}
                toggleShowPassword={() => setShowPasswords((state) => !state)}
              />
            </InputContainer>
          ))}
        </Inputs>

        <SubmitButton
          size="xsmall"
          rounded
          icon="end"
          type="submit"
          disabled={errorsInForm}
        >
          {t('reset_password_button_submit')}
          <SubmitIcon aria-hidden />
        </SubmitButton>

        {resetPasswordError && (
          <ErrorBlock>
            {/* FIXME: This is not localized. */}
            {resetPasswordError.responseContent?.detail ||
              resetPasswordError.responseContent?.title ||
              resetPasswordError.statusText}
          </ErrorBlock>
        )}
      </form>
    </Container>
  );
}
